import React, { useState, useEffect, useCallback, useRef } from "react";
import {
	Box,
	Typography,
	Card,
	Button,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	LinearProgress,
	IconButton,
	Snackbar,
	Alert,
	Tabs,
	Tab,
	Chip,
	Checkbox,
	Switch,
	FormControlLabel,
	Autocomplete,
} from "@mui/material";
import AppLayout from "../../components/AppLayout";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SendIcon from "@mui/icons-material/Send";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SettingsIcon from "@mui/icons-material/Settings";
import MessageIcon from "@mui/icons-material/Message";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import WhatsappService from "../../service/whatsapp_service";
import FornecedorService from "../../service/fornecedor_service";
import ColetaService from "../../service/coleta_service";
import ProdutoService from "../../service/produto_service";
import UnidadeMedidaService from "../../service/unidade_medida_service";
import { UnidadeDistribuidorService } from "../../service/unidade_distribuidor_service";
import { hojeISO } from "../../utils/dateUtils";

// Data de CALENDARIO (dataAlvo, dataInicio): o backend a ancora em meia-noite UTC,
// porque "o dia 15" nao tem hora. Formatar no fuso do navegador mostrava 14/09 para
// uma dataAlvo de 15/09 -- no Brasil (UTC-3), meia-noite UTC ainda e o dia anterior.
// Por isso timeZone UTC aqui: le-se o mesmo dia que o backend gravou.
const formatDateBR = (dateStr) => {
	if (!dateStr) return "";
	const d = new Date(dateStr);
	if (Number.isNaN(d.getTime())) return "";
	return d.toLocaleDateString("pt-BR", { timeZone: "UTC" });
};

// Instante de verdade (enviadaEm, respondidaEm): aqui a hora importa e o fuso do
// navegador e o certo -- "enviada as 10:50" deve ser 10:50 para quem le a tela.
const formatDateTimeBR = (dateStr) => {
	if (!dateStr) return "";
	const d = new Date(dateStr);
	if (Number.isNaN(d.getTime())) return "";
	return d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

// Template aprovado na Meta (lembrete_cliente / pt_BR), copiado do corpo
// aprovado. É este texto que o fornecedor lê, com os botões Sim / Não embaixo. Se
// mudar o template na Meta, atualize aqui e em n8n/README.md.
const TEMPLATE_MENSAGEM =
	"Olá {nome}! A {distribuidora} gostaria de saber a disponibilidade dos seus produtos para {data}.\n\n" +
	"Seus produtos e embalagens: {produtos}.\n\n" +
	"Você pode mudar o que responder até {prazo}. Depois dessa data, fale direto com a distribuidora.\n\n" +
	"Possui esses produtos para a data informada?\n\n" +
	"[ Sim ]   [ Não ]";

// Escolha de culturas do envio/agendamento. Nada marcado = todas as culturas.
// Cultura salva que saiu da lista (ninguem mais fornece) continua visivel, para
// nao sumir da agenda em silencio ao salvar.
function SeletorCulturas({ culturas, value, onChange, helperText }) {
	const conhecidas = new Map(culturas.map((c) => [c.id, c]));
	const selecionadas = value.map((id) => conhecidas.get(id) || { id, nome: "Cultura indisponível", fornecedorIds: [] });
	return (
		<Autocomplete
			multiple
			disableCloseOnSelect
			options={culturas}
			value={selecionadas}
			onChange={(_, sel) => onChange(sel.map((c) => c.id))}
			getOptionLabel={(c) => c.nome}
			isOptionEqualToValue={(a, b) => a.id === b.id}
			noOptionsText="Nenhuma cultura fornecida pelos fornecedores desta unidade"
			renderInput={(params) => (
				<TextField
					{...params}
					label="Culturas"
					placeholder={value.length ? "" : "Todas as culturas"}
					helperText={helperText}
					InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
				/>
			)}
		/>
	);
}

function WhatsApp() {
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState(0);
	const [loading, setLoading] = useState(false);
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

	// Data from API
	const [messages, setMessages] = useState([]);
	const [stats, setStats] = useState({ total: 0, enviadas: 0, respondidas: 0, pendentes: 0, falhas: 0, taxa: 0 });

	// Suppliers from fornecedores
	const [suppliers, setSuppliers] = useState([]);

	const [distribuidoraNome, setDistribuidoraNome] = useState("");

	// A unidade pode ter varios agendamentos: grupos diferentes de fornecedores,
	// perguntados em dias diferentes sobre datas diferentes.
	const [agendamentos, setAgendamentos] = useState([]);

	// Formulario de agendamento (criar/editar). editandoId = null e criacao.
	// dataInicio = primeiro envio; dataAlvo = dia sobre o qual se pergunta. A
	// recorrencia e fixa a cada 7 dias e AMBAS avancam +7 por ciclo, mantendo o
	// intervalo entre elas.
	const [formDialogOpen, setFormDialogOpen] = useState(false);
	const [editandoId, setEditandoId] = useState(null);
	const [formNome, setFormNome] = useState("");
	const [dataInicio, setDataInicio] = useState("");
	const [dataAlvo, setDataAlvo] = useState("");
	// Ultimo dia em que o fornecedor pode mudar pelo WhatsApp o que respondeu.
	// Opcional; avanca +7 por ciclo junto com as outras duas datas.
	const [dataLimiteRetificacao, setDataLimiteRetificacao] = useState("");
	// Hora (horario de Brasilia) em que o prazo fecha no dia limite. A hora fica a
	// mesma em todo ciclo; so a data avanca.
	const [horaLimiteRetificacao, setHoraLimiteRetificacao] = useState("");
	const [hora, setHora] = useState("08:00");
	const [formAtivo, setFormAtivo] = useState(true);
	// Quem recebe. enviarParaTodos = true inclui ate os fornecedores cadastrados
	// depois; com false, valem so os ids marcados.
	const [enviarParaTodos, setEnviarParaTodos] = useState(true);
	const [fornecedoresSelecionados, setFornecedoresSelecionados] = useState([]);
	// Culturas do agendamento ([] = todas). So recebe quem fornece todas elas.
	const [formCultivoIds, setFormCultivoIds] = useState([]);

	// Culturas que podem ser pedidas, cada uma com os ids de quem a fornece.
	const [culturas, setCulturas] = useState([]);

	// Confirmacao de exclusao: apagar um agendamento para os envios futuros, e
	// nao da para desfazer -- nao pode acontecer com um clique so.
	const [excluindo, setExcluindo] = useState(null);

	// Lists for coleta creation
	const [fornecedores, setFornecedores] = useState([]);
	const [produtos, setProdutos] = useState([]);
	const [unidadesMedida, setUnidadesMedida] = useState([]);

	// Coleta dialog
	const [coletaDialogOpen, setColetaDialogOpen] = useState(false);
	const [selectedMessage, setSelectedMessage] = useState(null);
	const [formProduto, setFormProduto] = useState("");
	const [formQuantidade, setFormQuantidade] = useState("");
	const [formUnidadeMedida, setFormUnidadeMedida] = useState("");
	const [formFornecedor, setFormFornecedor] = useState("");
	const [formDataPrevista, setFormDataPrevista] = useState("");

	// Enviar dialog
	const [enviarDialogOpen, setEnviarDialogOpen] = useState(false);
	const [enviarDataAlvo, setEnviarDataAlvo] = useState("");
	const [enviarLimiteRetificacao, setEnviarLimiteRetificacao] = useState("");
	const [enviarHoraLimite, setEnviarHoraLimite] = useState("");
	// Destinatarios deste envio avulso. Nasce da selecao da agenda, mas alterar
	// aqui nao mexe na configuracao -- e um envio pontual.
	const [enviarFornecedorIds, setEnviarFornecedorIds] = useState([]);
	const [enviarCultivoIds, setEnviarCultivoIds] = useState([]);

	const getUnidadeId = () => localStorage.getItem("selected_unidade_id");

	// Dia dos cartoes de movimento. Comeca em hoje; o usuario escolhe outro no
	// calendario. Ref para o recarregar depois de um envio usar o dia escolhido sem
	// recriar loadMensagens (e recarregar a lista) a cada troca de dia.
	const [diaEstatisticas, setDiaEstatisticas] = useState(hojeISO());
	const diaEstatisticasRef = useRef(diaEstatisticas);

	const loadEstatisticas = useCallback(async (dia) => {
		const unidadeId = getUnidadeId();
		if (!unidadeId) return;
		const vazio = { total: 0, enviadas: 0, respondidas: 0, pendentes: 0, falhas: 0, taxa: 0, dia };
		const statsRes = await new WhatsappService().getEstatisticas(unidadeId, dia).catch(() => ({ data: vazio }));
		setStats(statsRes.data || vazio);
	}, []);

	const loadMensagens = useCallback(async () => {
		try {
			setLoading(true);
			const unidadeId = getUnidadeId();
			if (!unidadeId) return;

			const whatsappService = new WhatsappService();
			const [mensagensRes] = await Promise.all([
				whatsappService.getMensagens(unidadeId).catch(() => ({ data: { data: [] } })),
				loadEstatisticas(diaEstatisticasRef.current),
			]);

			const mensagensData = mensagensRes.data?.data || mensagensRes.data || [];
			setMessages(Array.isArray(mensagensData) ? mensagensData : []);
		} catch (error) {
			console.error("Erro ao carregar mensagens:", error);
		} finally {
			setLoading(false);
		}
	}, [loadEstatisticas]);

	const trocarDiaEstatisticas = (dia) => {
		if (!dia) return;
		setDiaEstatisticas(dia);
		diaEstatisticasRef.current = dia;
		loadEstatisticas(dia);
	};

	const loadAgendamentos = useCallback(async () => {
		try {
			const unidadeId = getUnidadeId();
			if (!unidadeId) return;

			const whatsappService = new WhatsappService();
			const unidadeService = new UnidadeDistribuidorService();
			const [agendamentosRes, unidade] = await Promise.all([
				whatsappService.listarAgendamentos(unidadeId).catch(() => ({ data: [] })),
				unidadeService.getById(unidadeId).catch(() => null),
			]);

			setAgendamentos(Array.isArray(agendamentosRes.data) ? agendamentosRes.data : []);

			const unidadeData = unidade?.data || unidade;
			if (unidadeData) {
				setDistribuidoraNome(unidadeData.nomeFantasia || unidadeData.nome || "");
			}
		} catch (error) {
			console.error("Erro ao carregar agendamentos:", error);
		}
	}, []);

	const loadLists = useCallback(async () => {
		try {
			const unidadeId = getUnidadeId();
			if (!unidadeId) return;

			const fornecedorService = new FornecedorService();
			const produtoService = new ProdutoService();
			const unidadeMedidaService = new UnidadeMedidaService();

			const [fornecedoresRes, produtosRes, unidadesRes, culturasRes] = await Promise.all([
				fornecedorService.getByUnidade(unidadeId).catch(() => []),
				produtoService.getByUnidade(unidadeId, "entrada").catch(() => []),
				unidadeMedidaService.getAllByCultivo().catch(() => []),
				new WhatsappService().listarCulturas(unidadeId).catch(() => ({ data: [] })),
			]);

			setCulturas(Array.isArray(culturasRes.data) ? culturasRes.data : []);

			const fornecedoresData = Array.isArray(fornecedoresRes) ? fornecedoresRes : (fornecedoresRes.data || []);
			const produtosData = Array.isArray(produtosRes) ? produtosRes : (produtosRes.data || []);
			const unidadesData = Array.isArray(unidadesRes) ? unidadesRes : (unidadesRes.data || []);

			setFornecedores(fornecedoresData);
			setProdutos(produtosData);
			setUnidadesMedida(unidadesData);

			setSuppliers(fornecedoresData.map((f) => ({
				id: f.id,
				name: f.nome,
				phone: f.telefone || "",
				active: !f.oculto,
			})));
		} catch (error) {
			console.error("Erro ao carregar listas:", error);
		}
	}, []);

	useEffect(() => {
		loadMensagens();
		loadAgendamentos();
		loadLists();
	}, [loadMensagens, loadAgendamentos, loadLists]);

	// Datas vem como @db.Date (meia-noite UTC); pega so a parte YYYY-MM-DD para o
	// input date, sem passar pelo fuso local.
	const paraInputDate = (valor) => (valor ? new Date(valor).toISOString().split("T")[0] : "");

	// Formata YYYY-MM-DD -> DD/MM/YYYY sem passar por Date (evita off-by-one de fuso).
	const br = (d) => (d ? d.split("-").reverse().join("/") : "");

	// "HH:mm" de agora, no relogio do navegador (o usuario esta no Brasil).
	const horaAgora = () => new Date().toTimeString().slice(0, 5);

	// Prazo como a mensagem do fornecedor mostra: "07/10/2026 as 17:00".
	const prazoBR = (data, hora) => {
		const dia = formatDateBR(data);
		return dia && hora ? `${dia} às ${hora}` : dia;
	};

	const abrirNovoAgendamento = () => {
		setEditandoId(null);
		setFormNome("");
		setDataInicio("");
		setDataAlvo("");
		setDataLimiteRetificacao("");
		setHoraLimiteRetificacao("");
		setHora("08:00");
		setFormAtivo(true);
		setEnviarParaTodos(true);
		setFornecedoresSelecionados([]);
		setFormCultivoIds([]);
		setFormDialogOpen(true);
	};

	const abrirEdicaoAgendamento = (a) => {
		setEditandoId(a.id);
		setFormNome(a.nome || "");
		setDataInicio(paraInputDate(a.dataInicio));
		setDataAlvo(paraInputDate(a.dataAlvo));
		setDataLimiteRetificacao(paraInputDate(a.dataLimiteRetificacao));
		// Agendamento anterior ao campo nao tem hora: a tela exige escolher ao salvar.
		setHoraLimiteRetificacao(a.horaLimiteRetificacao || "");
		setHora(a.horario || "08:00");
		setFormAtivo(a.ativo !== false);
		setEnviarParaTodos(a.enviarParaTodos !== false);
		setFornecedoresSelecionados((a.fornecedoresSelecionados || []).map((f) => f.fornecedorId));
		setFormCultivoIds(a.cultivoIds || []);
		setFormDialogOpen(true);
	};

	const handleSalvarAgendamento = async () => {
		try {
			const unidadeId = getUnidadeId();
			if (!unidadeId) return;

			if (!dataInicio || !dataAlvo) {
				setSnackbar({ open: true, message: "Selecione a data de envio e a data de recebimento", severity: "error" });
				return;
			}

			// Obrigatoria: vai no template como {{5}}, e a Meta recusa variavel vazia.
			if (!dataLimiteRetificacao || !horaLimiteRetificacao) {
				setSnackbar({ open: true, message: "Informe a data e a hora limite de retificacao", severity: "error" });
				return;
			}

			if (dataAlvo < dataInicio) {
				setSnackbar({ open: true, message: "A data de recebimento nao pode ser anterior a data de envio", severity: "error" });
				return;
			}

			if (dataLimiteRetificacao < dataInicio || dataLimiteRetificacao > dataAlvo) {
				setSnackbar({ open: true, message: "A data limite de retificacao deve ficar entre a data de envio e a data de recebimento", severity: "error" });
				return;
			}

			// No proprio dia do envio, um prazo que fecha antes do horario de envio
			// chegaria ao fornecedor ja vencido.
			if (dataLimiteRetificacao === dataInicio && horaLimiteRetificacao <= hora) {
				setSnackbar({ open: true, message: "No dia do envio, a hora limite de retificacao deve ser depois da hora de envio", severity: "error" });
				return;
			}

			// Quem nao fornece todas as culturas escolhidas o backend recusa.
			const elegiveisAgenda = new Set(fornecedoresDasCulturas(formCultivoIds).map((s) => s.id));
			const selecaoValida = fornecedoresSelecionados.filter((id) => elegiveisAgenda.has(id));

			if (formCultivoIds.length > 0 && elegiveisAgenda.size === 0) {
				setSnackbar({ open: true, message: "Nenhum fornecedor fornece todas as culturas escolhidas", severity: "error" });
				return;
			}

			// O backend aceita selecao vazia como "nao enviar para ninguem", mas aqui
			// isso quase sempre e engano: o agendamento pararia de enviar em silencio.
			if (!enviarParaTodos && selecaoValida.length === 0) {
				setSnackbar({ open: true, message: "Selecione ao menos um fornecedor ou marque \"Enviar para todos\"", severity: "error" });
				return;
			}

			const payload = {
				unidadeDistribuidoraId: unidadeId,
				nome: formNome.trim(),
				dataInicio,
				dataAlvo,
				dataLimiteRetificacao,
				horaLimiteRetificacao,
				horario: hora,
				ativo: formAtivo,
				enviarParaTodos,
				// Com "todos" ligado a lista nao e enviada: o backend mantem a selecao
				// anterior guardada, entao desligar o toggle a traz de volta.
				fornecedorIds: enviarParaTodos ? undefined : selecaoValida,
				cultivoIds: formCultivoIds,
			};

			const whatsappService = new WhatsappService();
			if (editandoId) {
				await whatsappService.atualizarAgendamento(editandoId, payload);
			} else {
				await whatsappService.criarAgendamento(payload);
			}

			const publico = (enviarParaTodos
				? "todos os fornecedores"
				: `${selecaoValida.length} fornecedor(es) selecionado(s)`) +
				(formCultivoIds.length ? ` (culturas: ${nomesCulturas(formCultivoIds)})` : "");
			setFormDialogOpen(false);
			setSnackbar({
				open: true,
				message:
					`Agendamento salvo! Primeiro envio em ${br(dataInicio)} as ${hora} para ${publico}, com recebimento em ${br(dataAlvo)}` +
					`, alteracoes ate ${br(dataLimiteRetificacao)} as ${horaLimiteRetificacao}, e a cada 7 dias.`,
				severity: "success",
			});
			loadAgendamentos();
		} catch (error) {
			console.error("Erro ao salvar agendamento:", error);
			// 400 do backend traz o motivo (ex.: fornecedor de outra unidade).
			const motivo = error?.response?.data?.message;
			setSnackbar({
				open: true,
				message: motivo ? `Erro ao salvar agendamento: ${motivo}` : "Erro ao salvar agendamento",
				severity: "error",
			});
		}
	};

	const handleExcluirAgendamento = async () => {
		if (!excluindo) return;
		try {
			const whatsappService = new WhatsappService();
			await whatsappService.excluirAgendamento(excluindo.id);
			setExcluindo(null);
			setSnackbar({ open: true, message: "Agendamento excluido. As mensagens ja enviadas continuam no historico.", severity: "success" });
			loadAgendamentos();
		} catch (error) {
			console.error("Erro ao excluir agendamento:", error);
			const motivo = error?.response?.data?.message;
			setExcluindo(null);
			setSnackbar({
				open: true,
				message: motivo ? `Erro ao excluir agendamento: ${motivo}` : "Erro ao excluir agendamento",
				severity: "error",
			});
		}
	};

	// Fornecedores que podem receber: sem telefone o backend descarta em silencio,
	// e id de fornecedor oculto ele recusa com 400 -- em nenhum dos dois casos faz
	// sentido a tela deixar marcar.
	const fornecedoresElegiveis = suppliers.filter((s) => s.active && s.phone);

	// Com culturas escolhidas, so quem fornece TODAS elas pode receber -- a
	// pergunta e sobre aquelas culturas, e quem nao tem uma delas nao responde.
	const fornecedoresDasCulturas = (cultivoIds) => {
		if (!cultivoIds.length) return fornecedoresElegiveis;
		const conjuntos = cultivoIds.map((id) => new Set(culturas.find((c) => c.id === id)?.fornecedorIds || []));
		return fornecedoresElegiveis.filter((s) => conjuntos.every((c) => c.has(s.id)));
	};

	const nomesCulturas = (cultivoIds) =>
		(cultivoIds || []).map((id) => culturas.find((c) => c.id === id)?.nome || "?").join(", ");

	const elegiveisEnvio = fornecedoresDasCulturas(enviarCultivoIds);
	const elegiveisAgenda = fornecedoresDasCulturas(formCultivoIds);

	const alternarSelecao = (lista, setLista, id) =>
		setLista(lista.includes(id) ? lista.filter((x) => x !== id) : [...lista, id]);

	const abrirEnviarDialog = () => {
		// Envio avulso e independente dos agendamentos -- com varios deles nao ha
		// "o publico da unidade" de onde herdar. Comeca com todos marcados e o
		// usuario tira quem nao deve receber.
		setEnviarFornecedorIds(fornecedoresElegiveis.map((s) => s.id));
		setEnviarCultivoIds([]);
		setEnviarDataAlvo("");
		setEnviarLimiteRetificacao("");
		setEnviarHoraLimite("");
		setEnviarDialogOpen(true);
	};

	// Trocar as culturas muda quem pode receber: o envio volta a marcar todos os
	// que fornecem; a agenda so tira da selecao quem deixou de servir.
	const alterarCulturasEnvio = (ids) => {
		setEnviarCultivoIds(ids);
		setEnviarFornecedorIds(fornecedoresDasCulturas(ids).map((s) => s.id));
	};

	const alterarCulturasAgenda = (ids) => {
		setFormCultivoIds(ids);
		const aptos = new Set(fornecedoresDasCulturas(ids).map((s) => s.id));
		setFornecedoresSelecionados((sel) => sel.filter((id) => aptos.has(id)));
	};

	const handleEnviarMensagens = async () => {
		try {
			if (!enviarDataAlvo) {
				setSnackbar({ open: true, message: "Selecione a data de recebimento", severity: "error" });
				return;
			}

			if (enviarFornecedorIds.length === 0) {
				setSnackbar({ open: true, message: "Selecione ao menos um fornecedor", severity: "error" });
				return;
			}

			// O backend recusa as tres coisas; conferir aqui evita a ida e volta.
			// Obrigatoria: vai no template como {{5}}, e a Meta recusa variavel vazia.
			if (!enviarLimiteRetificacao || !enviarHoraLimite) {
				setSnackbar({ open: true, message: "Informe a data e a hora limite de retificacao", severity: "error" });
				return;
			}
			if (enviarLimiteRetificacao > enviarDataAlvo) {
				setSnackbar({ open: true, message: "A data limite de retificacao nao pode ser depois da data de recebimento", severity: "error" });
				return;
			}
			if (enviarLimiteRetificacao < hojeISO()) {
				setSnackbar({ open: true, message: "A data limite de retificacao nao pode ser anterior a hoje", severity: "error" });
				return;
			}
			// Hoje com hora que ja passou: o fornecedor receberia o prazo vencido.
			if (enviarLimiteRetificacao === hojeISO() && enviarHoraLimite <= horaAgora()) {
				setSnackbar({ open: true, message: "A hora limite de retificacao de hoje ja passou", severity: "error" });
				return;
			}

			const unidadeId = getUnidadeId();
			if (!unidadeId) return;

			const whatsappService = new WhatsappService();
			const result = await whatsappService.enviarMensagens(unidadeId, enviarDataAlvo, enviarFornecedorIds, enviarLimiteRetificacao, enviarCultivoIds, enviarHoraLimite);
			const count = Array.isArray(result.data) ? result.data.length : 0;

			setEnviarDialogOpen(false);
			setEnviarDataAlvo("");
			setEnviarLimiteRetificacao("");
			setEnviarHoraLimite("");
			setSnackbar({ open: true, message: `${count} mensagens enviadas aos fornecedores!`, severity: "success" });
			loadMensagens();
		} catch (error) {
			console.error("Erro ao enviar mensagens:", error);
			// O backend devolve 400 com o motivo (selecao vazia, fornecedor de outra
			// unidade). Mostrar so "erro ao enviar" escondia o que precisa ser corrigido.
			const motivo = error?.response?.data?.message;
			setSnackbar({
				open: true,
				message: motivo ? `Erro ao enviar mensagens: ${motivo}` : "Erro ao enviar mensagens",
				severity: "error",
			});
		}
	};

	const handleResend = async (msg) => {
		try {
			const whatsappService = new WhatsappService();
			await whatsappService.reenviarMensagem(msg.id);
			setSnackbar({ open: true, message: `Mensagem reenviada para ${msg.fornecedor?.nome || "fornecedor"}`, severity: "success" });
			loadMensagens();
		} catch (error) {
			console.error("Erro ao reenviar:", error);
			// Lembrete de dia que ja passou o backend recusa com o motivo (400).
			const motivo = error?.response?.data?.message;
			setSnackbar({
				open: true,
				message: motivo ? `Não foi possível reenviar: ${motivo}` : "Erro ao reenviar mensagem",
				severity: "error",
			});
		}
	};

	const handleResendAllPending = async () => {
		try {
			const unidadeId = getUnidadeId();
			if (!unidadeId) return;

			const whatsappService = new WhatsappService();
			const result = await whatsappService.reenviarPendentes(unidadeId);
			// Os de dia que ja passou ficam fora da fila: sairiam junto com as perguntas
			// atuais e confundiriam a resposta do fornecedor.
			const vencidas = result.data?.vencidas || 0;
			setSnackbar({
				open: true,
				message:
					`${result.data?.reenviadas || 0} mensagens reenviadas.` +
					(vencidas ? ` ${vencidas} não reenviada(s): a data de recebimento já passou.` : ""),
				severity: "success",
			});
			loadMensagens();
		} catch (error) {
			console.error("Erro ao reenviar pendentes:", error);
			setSnackbar({ open: true, message: "Erro ao reenviar mensagens pendentes", severity: "error" });
		}
	};

	const openColetaFromMessage = (msg) => {
		setSelectedMessage(msg);
		setFormProduto("");
		setFormQuantidade("");
		setFormUnidadeMedida("");
		setFormFornecedor(msg.fornecedorId || "");
		setFormDataPrevista(msg.dataAlvo ? new Date(msg.dataAlvo).toISOString().split("T")[0] : "");
		setColetaDialogOpen(true);
	};

	const handleSaveColeta = async () => {
		try {
			if (!formProduto || !formQuantidade || !formUnidadeMedida) {
				setSnackbar({ open: true, message: "Preencha todos os campos obrigatorios", severity: "error" });
				return;
			}

			const unidadeId = getUnidadeId();
			const coletaService = new ColetaService();
			const today = hojeISO();

			const data = {
				unidadeDistribuidoraId: unidadeId,
				fornecedorId: formFornecedor || null,
				produtoUnidadeDistribuidoraId: formProduto,
				unidadeMedidaId: formUnidadeMedida,
				data: today,
				quantidade: parseFloat(formQuantidade),
				dataPrevista: formDataPrevista || null,
				status: "PENDENTE",
				observacao: selectedMessage ? `Coleta via WhatsApp - Resposta de ${selectedMessage.fornecedor?.nome || "fornecedor"}` : "Coleta via WhatsApp",
			};

			const coletaResult = await coletaService.save(data);

			// Vincular coleta a mensagem
			if (selectedMessage && coletaResult.data?.id) {
				const whatsappService = new WhatsappService();
				await whatsappService.vincularColeta(selectedMessage.id, coletaResult.data.id).catch((e) => {
					console.error("Erro ao vincular coleta:", e);
				});
			}

			setColetaDialogOpen(false);
			setSelectedMessage(null);
			setSnackbar({ open: true, message: "Coleta registrada com sucesso a partir da resposta do WhatsApp!", severity: "success" });
			loadMensagens();
		} catch (error) {
			console.error("Erro ao salvar coleta:", error);
			setSnackbar({ open: true, message: "Erro ao registrar coleta", severity: "error" });
		}
	};

	const getStatusChip = ({ status, semEstoque }) => {
		// Respondeu "nao vou ter" (0 em tudo): sem isto a linha fica igual a de quem
		// prometeu produto, e o distribuidor so descobre abrindo Coletas.
		if (semEstoque) {
			return <Chip label="Não terá estoque" size="small" sx={{ bgcolor: "#8e44ad15", color: "#8e44ad", fontWeight: 600, fontSize: "0.7rem" }} />;
		}
		const statusLower = (status || "").toLowerCase();
		switch (statusLower) {
			case "respondida":
				return <Chip label="Respondida" size="small" sx={{ bgcolor: "#27ae6015", color: "#27ae60", fontWeight: 500, fontSize: "0.7rem" }} />;
			case "enviada":
				return <Chip label="Enviada" size="small" sx={{ bgcolor: "#3498db15", color: "#3498db", fontWeight: 500, fontSize: "0.7rem" }} />;
			case "pendente":
				return <Chip label="Pendente" size="small" sx={{ bgcolor: "#f5a62315", color: "#f5a623", fontWeight: 500, fontSize: "0.7rem" }} />;
			case "falha":
				return <Chip label="Falha" size="small" sx={{ bgcolor: "#e74c3c15", color: "#e74c3c", fontWeight: 500, fontSize: "0.7rem" }} />;
			default:
				return null;
		}
	};

	const pendentesEFalhas = stats.pendentes + stats.falhas;

	const statCards = [
		{ icon: <SendIcon sx={{ color: "#1a9e8f" }} />, value: stats.total, label: "Enviadas" },
		{ icon: <MarkEmailReadIcon sx={{ color: "#1a9e8f" }} />, value: stats.respondidas, label: "Respondidas" },
		{ icon: <AccessTimeIcon sx={{ color: "#f5a623" }} />, value: stats.pendentes + stats.enviadas, label: "Aguardando" },
		{ icon: <CheckCircleIcon sx={{ color: "#1a9e8f" }} />, value: `${stats.taxa}%`, label: "Taxa de Resposta" },
	];

	return (
		<AppLayout currentPage="coletas">
			<Box sx={{ maxWidth: 1200, mx: "auto" }}>
				{/* Header */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
					<IconButton onClick={() => navigate("/coletas")} sx={{ color: "#0f4557" }}>
						<ArrowBackIcon />
					</IconButton>
					<Box>
						<Typography variant="h4" sx={{ color: "#0f4557", fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
							Chatbot WhatsApp
						</Typography>
						<Typography variant="body2" sx={{ color: "#5a7a88", mt: 0.5 }}>
							Coleta automatica de previsoes dos fornecedores via WhatsApp
						</Typography>
					</Box>
				</Box>

				{loading && <LinearProgress sx={{ mb: 2 }} />}

				{/* Stats Cards -- o movimento de um dia (hoje, ou o escolhido). Sem este
				    rotulo os numeros parecem acumulados desde sempre. */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, flexWrap: "wrap" }}>
					<Typography variant="caption" sx={{ color: "#8e9bae" }}>
						Movimento do dia{diaEstatisticas === hojeISO() ? " (hoje)" : ""}
					</Typography>
					<TextField
						type="date"
						size="small"
						value={diaEstatisticas}
						onChange={(e) => trocarDiaEstatisticas(e.target.value)}
						inputProps={{ max: hojeISO(), "aria-label": "Dia do movimento" }}
						sx={{ width: 170, "& .MuiInputBase-input": { py: 0.75, fontSize: "0.85rem" } }}
					/>
					{diaEstatisticas !== hojeISO() && (
						<Button size="small" onClick={() => trocarDiaEstatisticas(hojeISO())} sx={{ textTransform: "none", color: "#1a9e8f" }}>
							Voltar para hoje
						</Button>
					)}
				</Box>
				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr 1fr" }, gap: 2, mb: 3 }}>
					{statCards.map((stat, i) => (
						<Card key={i} sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8", p: 2.5, textAlign: "center" }}>
							<Box sx={{ mb: 1 }}>{stat.icon}</Box>
							<Typography variant="h5" sx={{ fontWeight: 700, color: "#0f4557" }}>{stat.value}</Typography>
							<Typography variant="caption" sx={{ color: "#8e9bae" }}>{stat.label}</Typography>
						</Card>
					))}
				</Box>

				{/* Response Rate Bar */}
				<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8", p: 2.5, mb: 3 }}>
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
						<Typography variant="body2" sx={{ fontWeight: 500, color: "#0f4557" }}>Taxa de Resposta do Dia</Typography>
						<Typography variant="body2" sx={{ fontWeight: 700, color: "#1a9e8f" }}>{stats.taxa}%</Typography>
					</Box>
					<LinearProgress
						variant="determinate"
						value={stats.taxa}
						sx={{ height: 8, borderRadius: 4, bgcolor: "#dfe3e8", "& .MuiLinearProgress-bar": { borderRadius: 4, bgcolor: "#1a9e8f" } }}
					/>
				</Card>

				{/* Tabs */}
				<Tabs
					value={activeTab}
					onChange={(_, v) => setActiveTab(v)}
					sx={{
						mb: 3,
						bgcolor: "white",
						borderRadius: "12px",
						border: "1px solid #dfe3e8",
						"& .MuiTab-root": { textTransform: "none", fontWeight: 500 },
						"& .Mui-selected": { color: "#1a9e8f" },
						"& .MuiTabs-indicator": { bgcolor: "#1a9e8f" },
					}}
				>
					<Tab icon={<MessageIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Mensagens" />
					<Tab icon={<SettingsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Agendamentos" />
				</Tabs>

				{/* Messages Tab */}
				{activeTab === 0 && (
					<Box>
						{/* Action buttons */}
						<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 2 }}>
							<Button
								variant="contained"
								startIcon={<SendIcon />}
								onClick={abrirEnviarDialog}
								sx={{ bgcolor: "#25D366", "&:hover": { bgcolor: "#20bd5a" }, textTransform: "none" }}
							>
								Enviar Mensagens
							</Button>
							{pendentesEFalhas > 0 && (
								<Button
									variant="outlined"
									startIcon={<RefreshIcon />}
									onClick={handleResendAllPending}
									sx={{ borderColor: "#1a9e8f", color: "#1a9e8f", textTransform: "none" }}
								>
									Reenviar pendentes ({pendentesEFalhas})
								</Button>
							)}
						</Box>

						{/* Messages Table */}
						<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8", overflow: "hidden" }}>
							{messages.length === 0 && !loading ? (
								<Box sx={{ p: 4, textAlign: "center" }}>
									<WhatsAppIcon sx={{ fontSize: 48, color: "#dfe3e8", mb: 1 }} />
									<Typography variant="body2" sx={{ color: "#8e9bae" }}>
										Nenhuma mensagem enviada ainda
									</Typography>
									<Button
										size="small"
										onClick={abrirEnviarDialog}
										sx={{ mt: 1, color: "#25D366" }}
									>
										Enviar primeira mensagem
									</Button>
								</Box>
							) : (
								<>
									{/* Mobile Cards */}
									<Box sx={{ display: { xs: "block", md: "none" } }}>
										{messages.map((msg) => (
											<Box key={msg.id} sx={{ p: 2.5, borderBottom: "1px solid #f0f2f5" }}>
												<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
														<Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#1a9e8f15", display: "flex", alignItems: "center", justifyContent: "center" }}>
															<Typography variant="caption" sx={{ fontWeight: 700, color: "#1a9e8f" }}>{(msg.fornecedor?.nome || "?").charAt(0)}</Typography>
														</Box>
														<Typography variant="body2" sx={{ fontWeight: 600, color: "#0f4557" }}>{msg.fornecedor?.nome || "Fornecedor"}</Typography>
													</Box>
													{getStatusChip(msg)}
												</Box>
												<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}>
													<Typography variant="caption" sx={{ color: "#8e9bae" }}>Enviada: <strong style={{ color: "#0f4557" }}>{formatDateTimeBR(msg.enviadaEm)}</strong></Typography>
													<Typography variant="caption" sx={{ color: "#8e9bae" }}>Recebimento: <strong style={{ color: "#0f4557" }}>{formatDateBR(msg.dataAlvo)}</strong></Typography>
													{msg.cultivoIds?.length > 0 && (
														<Typography variant="caption" sx={{ color: "#8e9bae", gridColumn: "1 / -1" }}>Culturas: <strong style={{ color: "#0f4557" }}>{nomesCulturas(msg.cultivoIds)}</strong></Typography>
													)}
													{msg.dataLimiteRetificacao && (
														<Typography variant="caption" sx={{ color: "#8e9bae", gridColumn: "1 / -1" }}>Alteracoes ate: <strong style={{ color: "#0f4557" }}>{prazoBR(msg.dataLimiteRetificacao, msg.horaLimiteRetificacao)}</strong></Typography>
													)}
													{msg.respondidaEm && (
														<Typography variant="caption" sx={{ color: "#8e9bae", gridColumn: "1 / -1" }}>Respondida: <strong style={{ color: "#0f4557" }}>{formatDateTimeBR(msg.respondidaEm)}</strong></Typography>
													)}
												</Box>
												<Box sx={{ display: "flex", gap: 1 }}>
													{(msg.status === "PENDENTE" || msg.status === "FALHA" || msg.status === "ENVIADA") && !msg.coletaId && (
														<>
															<Button size="small" startIcon={<RefreshIcon />} onClick={() => handleResend(msg)} sx={{ color: "#1a9e8f", textTransform: "none", fontSize: "0.75rem" }}>
																Reenviar
															</Button>
															<Button size="small" startIcon={<WhatsAppIcon />} onClick={() => openColetaFromMessage(msg)} sx={{ color: "#25D366", textTransform: "none", fontSize: "0.75rem" }}>
																Criar Coleta
															</Button>
														</>
													)}
													{msg.status === "RESPONDIDA" && !msg.coletaId && (
														<Button size="small" startIcon={<WhatsAppIcon />} onClick={() => openColetaFromMessage(msg)} sx={{ color: "#25D366", textTransform: "none", fontSize: "0.75rem" }}>
															Criar Coleta
														</Button>
													)}
													{msg.coletaId && (
														<Chip label="Coleta criada" size="small" sx={{ bgcolor: "#1a9e8f15", color: "#1a9e8f", fontSize: "0.65rem" }} />
													)}
												</Box>
											</Box>
										))}
									</Box>

									{/* Desktop Table */}
									<Box sx={{ display: { xs: "none", md: "block" }, overflowX: "auto" }}>
										<Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
											<Box component="thead">
												<Box component="tr" sx={{ borderBottom: "1px solid #dfe3e8", bgcolor: "#f8f9fa" }}>
													<Box component="th" sx={{ textAlign: "left", px: 3, py: 2, fontWeight: 500, color: "#8e9bae" }}>Fornecedor</Box>
													<Box component="th" sx={{ textAlign: "left", px: 3, py: 2, fontWeight: 500, color: "#8e9bae" }}>Enviada em</Box>
													<Box component="th" sx={{ textAlign: "left", px: 3, py: 2, fontWeight: 500, color: "#8e9bae" }}>Recebimento</Box>
													<Box component="th" sx={{ textAlign: "left", px: 3, py: 2, fontWeight: 500, color: "#8e9bae" }}>Alteracoes ate</Box>
													<Box component="th" sx={{ textAlign: "left", px: 3, py: 2, fontWeight: 500, color: "#8e9bae" }}>Respondida em</Box>
													<Box component="th" sx={{ textAlign: "left", px: 3, py: 2, fontWeight: 500, color: "#8e9bae" }}>Status</Box>
													<Box component="th" sx={{ textAlign: "right", px: 3, py: 2, fontWeight: 500, color: "#8e9bae" }}>Acao</Box>
												</Box>
											</Box>
											<Box component="tbody">
												{messages.map((msg) => (
													<Box component="tr" key={msg.id} sx={{ borderBottom: "1px solid #f0f2f5", "&:hover": { bgcolor: "#f8f9fa" } }}>
														<Box component="td" sx={{ px: 3, py: 2, fontWeight: 500, color: "#0f4557" }}>
															{msg.fornecedor?.nome || "Fornecedor"}
															{msg.cultivoIds?.length > 0 && (
																<Typography variant="caption" sx={{ display: "block", color: "#8e9bae", fontWeight: 400 }}>
																	{nomesCulturas(msg.cultivoIds)}
																</Typography>
															)}
														</Box>
														<Box component="td" sx={{ px: 3, py: 2, color: "#8e9bae" }}>{formatDateTimeBR(msg.enviadaEm)}</Box>
														<Box component="td" sx={{ px: 3, py: 2, color: "#0f4557" }}>{formatDateBR(msg.dataAlvo)}</Box>
														<Box component="td" sx={{ px: 3, py: 2, color: "#8e9bae" }}>{msg.dataLimiteRetificacao ? prazoBR(msg.dataLimiteRetificacao, msg.horaLimiteRetificacao) : "—"}</Box>
														<Box component="td" sx={{ px: 3, py: 2, color: "#8e9bae" }}>{msg.respondidaEm ? formatDateTimeBR(msg.respondidaEm) : "\u2014"}</Box>
														<Box component="td" sx={{ px: 3, py: 2 }}>{getStatusChip(msg)}</Box>
														<Box component="td" sx={{ px: 3, py: 2, textAlign: "right" }}>
															<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
																{(msg.status === "PENDENTE" || msg.status === "FALHA" || msg.status === "ENVIADA") && (
																	<IconButton size="small" onClick={() => handleResend(msg)} title="Reenviar" sx={{ color: "#1a9e8f" }}>
																		<RefreshIcon fontSize="small" />
																	</IconButton>
																)}
																{!msg.coletaId && (
																	<Button
																		size="small"
																		variant="outlined"
																		startIcon={<WhatsAppIcon />}
																		onClick={() => openColetaFromMessage(msg)}
																		sx={{ borderColor: "#25D366", color: "#25D366", textTransform: "none", fontSize: "0.75rem", "&:hover": { borderColor: "#20bd5a", bgcolor: "#25D36610" } }}
																	>
																		Criar Coleta
																	</Button>
																)}
																{msg.coletaId && (
																	<Chip label="Coleta criada" size="small" sx={{ bgcolor: "#1a9e8f15", color: "#1a9e8f", fontSize: "0.7rem" }} />
																)}
															</Box>
														</Box>
													</Box>
												))}
											</Box>
										</Box>
									</Box>
								</>
							)}
						</Card>
					</Box>
				)}

				{/* Agendamentos Tab */}
				{activeTab === 1 && (
					<Box>
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 2 }}>
							<Box>
								<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557" }}>
									Agendamentos ({agendamentos.length})
								</Typography>
								<Typography variant="caption" sx={{ color: "#8e9bae", display: "block" }}>
									Cada agendamento tem suas datas, horario e lista de fornecedores. Use mais de um para perguntar a grupos diferentes em dias diferentes.
								</Typography>
								{/* Fornecedor inativo ou sem telefone nao recebe de jeito nenhum; sem
								    este aviso a distribuidora so descobre pela ausencia de resposta. */}
								{suppliers.length > fornecedoresElegiveis.length && (
									<Typography variant="caption" sx={{ color: "#b26a00", display: "block" }}>
										{fornecedoresElegiveis.length} de {suppliers.length} fornecedores podem receber; os demais estao inativos ou sem telefone.
									</Typography>
								)}
							</Box>
							<Button
								variant="contained"
								startIcon={<AddIcon />}
								onClick={abrirNovoAgendamento}
								sx={{ bgcolor: "#1a9e8f", "&:hover": { bgcolor: "#158f80" }, textTransform: "none" }}
							>
								Novo agendamento
							</Button>
						</Box>

						{agendamentos.length === 0 ? (
							<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8", p: 3, mb: 3 }}>
								<Box sx={{ textAlign: "center", py: 4 }}>
									<AccessTimeIcon sx={{ fontSize: 48, color: "#dfe3e8", mb: 1 }} />
									<Typography variant="body2" sx={{ color: "#8e9bae" }}>Nenhum agendamento cadastrado</Typography>
									<Typography variant="caption" sx={{ color: "#8e9bae" }}>
										Sem agendamento, os lembretes so saem por envio avulso na aba Mensagens.
									</Typography>
								</Box>
							</Card>
						) : (
							<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2, mb: 3 }}>
								{agendamentos.map((a) => {
									const selecionados = a.fornecedoresSelecionados?.length || 0;
									return (
										<Card key={a.id} sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8", p: 3 }}>
											<Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 2 }}>
												<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
													<SettingsIcon sx={{ color: "#1a9e8f" }} />
													<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557" }}>
														{a.nome || `Envio de ${br(paraInputDate(a.dataInicio))}`}
													</Typography>
												</Box>
												<Chip
													label={a.ativo ? "Ativo" : "Pausado"}
													size="small"
													sx={{
														bgcolor: a.ativo ? "#1a9e8f15" : "#8e9bae15",
														color: a.ativo ? "#1a9e8f" : "#8e9bae",
														fontWeight: 500,
														fontSize: "0.7rem",
													}}
												/>
											</Box>

											<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 2 }}>
												<Typography variant="body2" sx={{ color: "#0f4557" }}>
													<strong>Primeiro envio:</strong> {br(paraInputDate(a.dataInicio))} as {a.horario}
												</Typography>
												<Typography variant="body2" sx={{ color: "#0f4557" }}>
													<strong>Recebimento:</strong> {br(paraInputDate(a.dataAlvo))}
												</Typography>
												<Typography variant="body2" sx={{ color: "#0f4557" }}>
													<strong>Alteracoes ate:</strong>{" "}
													{a.dataLimiteRetificacao
														? `${br(paraInputDate(a.dataLimiteRetificacao))}${a.horaLimiteRetificacao ? ` as ${a.horaLimiteRetificacao}` : ""}`
														: "sem prazo"}
												</Typography>
												<Typography variant="body2" sx={{ color: "#0f4557" }}>
													<strong>Culturas:</strong>{" "}
													{a.cultivoIds?.length ? nomesCulturas(a.cultivoIds) : "todas"}
												</Typography>
												<Typography variant="body2" sx={{ color: "#0f4557" }}>
													<strong>Recebem:</strong>{" "}
													{a.enviarParaTodos
														? a.cultivoIds?.length
															? "todos os fornecedores ativos que fornecem essas culturas"
															: "todos os fornecedores ativos"
														: `${selecionados} fornecedor(es) selecionado(s)`}
												</Typography>
												<Typography variant="caption" sx={{ color: "#8e9bae" }}>
													Repete a cada 7 dias; as duas datas avancam junto, mantendo o intervalo entre elas.
												</Typography>
												{/* Selecao vazia com "todos" desligado nao envia para ninguem -- o
												    agendamento fica parado sem estar pausado, entao avisamos. */}
												{!a.enviarParaTodos && selecionados === 0 && (
													<Typography variant="caption" sx={{ color: "#d32f2f" }}>
														Nenhum fornecedor selecionado: este agendamento nao vai enviar nada.
													</Typography>
												)}
											</Box>

											<Box sx={{ display: "flex", gap: 1 }}>
												<Button
													size="small"
													startIcon={<EditIcon />}
													onClick={() => abrirEdicaoAgendamento(a)}
													sx={{ textTransform: "none", color: "#1a9e8f" }}
												>
													Editar
												</Button>
												<Button
													size="small"
													startIcon={<DeleteIcon />}
													onClick={() => setExcluindo(a)}
													sx={{ textTransform: "none", color: "#d32f2f" }}
												>
													Excluir
												</Button>
											</Box>
										</Card>
									);
								})}
							</Box>
						)}

						{/* Message Template */}
						<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8", p: 3 }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
								<MessageIcon sx={{ color: "#1a9e8f" }} />
								<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557" }}>Mensagem</Typography>
							</Box>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
								<Typography variant="caption" sx={{ color: "#8e9bae" }}>
									A mensagem é enviada por um template fixo do WhatsApp e não pode ser editada. Vale para todos os agendamentos. Veja abaixo o texto que o fornecedor recebe.
								</Typography>
								<Box sx={{ bgcolor: "#f8f9fa", borderRadius: "12px", p: 2, border: "1px solid #dfe3e8" }}>
									<Typography variant="overline" sx={{ color: "#8e9bae", fontSize: "0.6rem" }}>Pre-visualizacao</Typography>
									<Typography variant="body2" sx={{ color: "#0f4557", whiteSpace: "pre-line", mt: 0.5 }}>
										{TEMPLATE_MENSAGEM
											.replace("{nome}", "Lucas")
											.replace("{distribuidora}", distribuidoraNome || "sua distribuidora")
											.replace("{data}", "10/03/2026")
											.replace("{produtos}", "Alface: un | Tomate: kg, Caixa (20 kg)")
											.replace("{prazo}", "09/03/2026")}
									</Typography>
								</Box>
							</Box>
						</Card>
					</Box>
				)}
			</Box>

			{/* Agendamento: criar / editar */}
			<Dialog open={formDialogOpen} onClose={() => setFormDialogOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<SettingsIcon sx={{ color: "#1a9e8f" }} />
						{editandoId ? "Editar agendamento" : "Novo agendamento"}
					</Box>
				</DialogTitle>
				<DialogContent>
					<Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2.5 }}>
						<TextField
							label="Nome (opcional)"
							placeholder="Ex.: Folhosas, Frutas da serra"
							value={formNome}
							onChange={(e) => setFormNome(e.target.value)}
							fullWidth
							InputLabelProps={{ shrink: true }}
						/>
						<TextField
							label="Data de envio (primeiro envio)"
							type="date"
							value={dataInicio}
							onChange={(e) => setDataInicio(e.target.value)}
							fullWidth
							InputLabelProps={{ shrink: true }}
						/>
						<TextField label="Hora de envio" type="time" value={hora} onChange={(e) => setHora(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
						<TextField
							label="Data de recebimento"
							type="date"
							value={dataAlvo}
							onChange={(e) => setDataAlvo(e.target.value)}
							fullWidth
							InputLabelProps={{ shrink: true }}
							helperText="Dia em que a distribuidora vai receber os produtos. E sobre este dia que o fornecedor e perguntado."
						/>
						<Box>
							<Box sx={{ display: "flex", gap: 2 }}>
								<TextField
									label="Data limite de retificacao"
									type="date"
									required
									value={dataLimiteRetificacao}
									onChange={(e) => setDataLimiteRetificacao(e.target.value)}
									sx={{ flex: 2, minWidth: 0 }}
									InputLabelProps={{ shrink: true }}
									inputProps={{ min: dataInicio || undefined, max: dataAlvo || undefined }}
									error={!dataLimiteRetificacao}
								/>
								<TextField
									label="Hora limite"
									type="time"
									required
									value={horaLimiteRetificacao}
									onChange={(e) => setHoraLimiteRetificacao(e.target.value)}
									sx={{ flex: 1, minWidth: 0 }}
									InputLabelProps={{ shrink: true }}
									error={!horaLimiteRetificacao}
								/>
							</Box>
							<Typography variant="caption" sx={{ color: "#8e9bae", display: "block", mt: 0.5, mx: 1.75 }}>
								Ate quando o fornecedor pode mudar pelo WhatsApp as quantidades que informou (horario de Brasilia). Vai na propria mensagem que ele recebe, e a data avanca 7 dias a cada ciclo.
							</Typography>
						</Box>
						<Typography variant="caption" sx={{ color: "#8e9bae" }}>
							O lembrete e enviado na data de envio, perguntando sobre a data de recebimento, e se repete a cada 7 dias. A cada ciclo, as datas avancam 7 dias.
						</Typography>

						<FormControlLabel
							control={<Switch checked={formAtivo} onChange={(e) => setFormAtivo(e.target.checked)} />}
							label={
								<Typography variant="body2" sx={{ color: "#0f4557" }}>
									Ativo {formAtivo ? "" : "(pausado: nao envia, mas continua salvo)"}
								</Typography>
							}
						/>

						<Box sx={{ borderTop: "1px solid #f0f2f5", pt: 2 }}>
							<SeletorCulturas
								culturas={culturas}
								value={formCultivoIds}
								onChange={alterarCulturasAgenda}
								helperText={
									formCultivoIds.length
										? `So recebe quem fornece todas as culturas escolhidas (${elegiveisAgenda.length} fornecedor(es)), e a mensagem pede so os dados delas.`
										: "Sem culturas escolhidas, a mensagem pede todos os produtos de cada fornecedor."
								}
							/>
						</Box>

						<Box sx={{ borderTop: "1px solid #f0f2f5", pt: 2 }}>
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
								<Typography variant="body2" sx={{ fontWeight: 600, color: "#0f4557" }}>
									Quem recebe
								</Typography>
								<FormControlLabel
									control={<Switch checked={enviarParaTodos} onChange={(e) => setEnviarParaTodos(e.target.checked)} />}
									label={<Typography variant="body2" sx={{ color: "#0f4557" }}>Enviar para todos</Typography>}
								/>
							</Box>

							<Typography variant="caption" sx={{ color: "#8e9bae", display: "block", mb: 1 }}>
								{enviarParaTodos
									? formCultivoIds.length
										? "Todos os fornecedores ativos com telefone que fornecem todas as culturas escolhidas recebem, inclusive os que forem cadastrados depois."
										: "Todos os fornecedores ativos com telefone recebem, inclusive os que forem cadastrados depois."
									: `Somente os marcados recebem (${fornecedoresSelecionados.length} de ${elegiveisAgenda.length}). Fornecedores cadastrados depois nao entram sozinhos.`}
							</Typography>

							{!enviarParaTodos && (
								<>
									{elegiveisAgenda.length > 0 && (
										<Box sx={{ display: "flex", gap: 1, mb: 1 }}>
											<Button
												size="small"
												onClick={() => setFornecedoresSelecionados(elegiveisAgenda.map((s) => s.id))}
												sx={{ textTransform: "none", color: "#1a9e8f" }}
											>
												Marcar todos
											</Button>
											<Button
												size="small"
												onClick={() => setFornecedoresSelecionados([])}
												sx={{ textTransform: "none", color: "#8e9bae" }}
											>
												Limpar
											</Button>
										</Box>
									)}

									{/* So elegiveis: sem telefone o backend descarta em silencio, e id de
									    fornecedor oculto ele recusa com 400. */}
									{elegiveisAgenda.length === 0 ? (
										<Typography variant="body2" sx={{ color: "#8e9bae", py: 2 }}>
											{formCultivoIds.length
												? "Nenhum fornecedor ativo com telefone fornece todas as culturas escolhidas."
												: "Nenhum fornecedor ativo com telefone cadastrado."}
										</Typography>
									) : (
										<Box sx={{ maxHeight: 240, overflowY: "auto", border: "1px solid #dfe3e8", borderRadius: "12px", px: 1 }}>
											{elegiveisAgenda.map((s, i) => (
												<Box
													key={s.id}
													sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, borderBottom: i < elegiveisAgenda.length - 1 ? "1px solid #f0f2f5" : "none" }}
												>
													<Checkbox
														checked={fornecedoresSelecionados.includes(s.id)}
														onChange={() => alternarSelecao(fornecedoresSelecionados, setFornecedoresSelecionados, s.id)}
														sx={{ color: "#dfe3e8", "&.Mui-checked": { color: "#1a9e8f" } }}
													/>
													<Box>
														<Typography variant="body2" sx={{ color: "#0f4557" }}>{s.name}</Typography>
														<Typography variant="caption" sx={{ color: "#8e9bae" }}>{s.phone}</Typography>
													</Box>
												</Box>
											))}
										</Box>
									)}
								</>
							)}
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setFormDialogOpen(false)}>Cancelar</Button>
					<Button
						variant="contained"
						onClick={handleSalvarAgendamento}
						sx={{ bgcolor: "#1a9e8f", "&:hover": { bgcolor: "#158f80" }, textTransform: "none" }}
					>
						Salvar
					</Button>
				</DialogActions>
			</Dialog>

			{/* Agendamento: confirmar exclusao */}
			<Dialog open={!!excluindo} onClose={() => setExcluindo(null)} maxWidth="xs" fullWidth>
				<DialogTitle>Excluir agendamento</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ color: "#0f4557" }}>
						Excluir <strong>{excluindo?.nome || "este agendamento"}</strong>? Os envios automaticos dele param.
					</Typography>
					<Typography variant="caption" sx={{ color: "#8e9bae", display: "block", mt: 1 }}>
						As mensagens ja enviadas continuam no historico; so o agendamento e removido.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setExcluindo(null)}>Cancelar</Button>
					<Button variant="contained" color="error" onClick={handleExcluirAgendamento} sx={{ textTransform: "none" }}>
						Excluir
					</Button>
				</DialogActions>
			</Dialog>

			{/* Enviar Mensagens Dialog */}
			<Dialog open={enviarDialogOpen} onClose={() => setEnviarDialogOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<WhatsAppIcon sx={{ color: "#25D366" }} />
						Enviar Mensagens
					</Box>
				</DialogTitle>
				<DialogContent>
					<Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
						<TextField
							label="Data de recebimento"
							type="date"
							value={enviarDataAlvo}
							onChange={(e) => setEnviarDataAlvo(e.target.value)}
							fullWidth
							InputLabelProps={{ shrink: true }}
							helperText="Dia em que a distribuidora vai receber os produtos."
						/>
						<Box>
							<Box sx={{ display: "flex", gap: 2 }}>
								<TextField
									label="Data limite de retificacao"
									type="date"
									required
									value={enviarLimiteRetificacao}
									onChange={(e) => setEnviarLimiteRetificacao(e.target.value)}
									sx={{ flex: 2, minWidth: 0 }}
									InputLabelProps={{ shrink: true }}
									inputProps={{ min: hojeISO(), max: enviarDataAlvo || undefined }}
									error={!enviarLimiteRetificacao}
								/>
								<TextField
									label="Hora limite"
									type="time"
									required
									value={enviarHoraLimite}
									onChange={(e) => setEnviarHoraLimite(e.target.value)}
									sx={{ flex: 1, minWidth: 0 }}
									InputLabelProps={{ shrink: true }}
									error={!enviarHoraLimite}
								/>
							</Box>
							<Typography variant="caption" sx={{ color: "#8e9bae", display: "block", mt: 0.5, mx: 1.75 }}>
								Ate quando o fornecedor pode mudar pelo WhatsApp as quantidades que informou (horario de Brasilia). Vai na propria mensagem que ele recebe.
							</Typography>
						</Box>

						<SeletorCulturas
							culturas={culturas}
							value={enviarCultivoIds}
							onChange={alterarCulturasEnvio}
							helperText={
								enviarCultivoIds.length
									? "A lista abaixo mostra so quem fornece todas as culturas escolhidas, e a mensagem pede so os dados delas."
									: "Sem culturas escolhidas, a mensagem pede todos os produtos de cada fornecedor."
							}
						/>

						<Box>
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
								<Typography variant="body2" sx={{ fontWeight: 600, color: "#0f4557" }}>
									Destinatarios ({enviarFornecedorIds.length} de {elegiveisEnvio.length})
								</Typography>
								<Box>
									<Button
										size="small"
										onClick={() => setEnviarFornecedorIds(elegiveisEnvio.map((s) => s.id))}
										sx={{ textTransform: "none", color: "#1a9e8f" }}
									>
										Marcar todos
									</Button>
									<Button
										size="small"
										onClick={() => setEnviarFornecedorIds([])}
										sx={{ textTransform: "none", color: "#8e9bae" }}
									>
										Limpar
									</Button>
								</Box>
							</Box>
							{/* Escolha valida so para este envio -- a agenda continua como esta. */}
							<Typography variant="caption" sx={{ color: "#8e9bae", display: "block", mb: 1 }}>
								Vale so para este envio; nao altera a configuracao da agenda.
							</Typography>

							{elegiveisEnvio.length === 0 ? (
								<Typography variant="body2" sx={{ color: "#8e9bae", py: 2 }}>
									{enviarCultivoIds.length
										? "Nenhum fornecedor ativo com telefone fornece todas as culturas escolhidas."
										: "Nenhum fornecedor ativo com telefone cadastrado."}
								</Typography>
							) : (
								<Box sx={{ maxHeight: 260, overflowY: "auto", border: "1px solid #dfe3e8", borderRadius: "12px", px: 1 }}>
									{elegiveisEnvio.map((s, i) => (
										<Box
											key={s.id}
											sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, borderBottom: i < elegiveisEnvio.length - 1 ? "1px solid #f0f2f5" : "none" }}
										>
											<Checkbox
												checked={enviarFornecedorIds.includes(s.id)}
												onChange={() => alternarSelecao(enviarFornecedorIds, setEnviarFornecedorIds, s.id)}
												sx={{ color: "#dfe3e8", "&.Mui-checked": { color: "#1a9e8f" } }}
											/>
											<Box>
												<Typography variant="body2" sx={{ color: "#0f4557" }}>{s.name}</Typography>
												<Typography variant="caption" sx={{ color: "#8e9bae" }}>{s.phone}</Typography>
											</Box>
										</Box>
									))}
								</Box>
							)}
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setEnviarDialogOpen(false)}>Cancelar</Button>
					<Button
						variant="contained"
						onClick={handleEnviarMensagens}
						disabled={enviarFornecedorIds.length === 0}
						sx={{ bgcolor: "#25D366", "&:hover": { bgcolor: "#20bd5a" } }}
					>
						Enviar {enviarFornecedorIds.length > 0 ? `(${enviarFornecedorIds.length})` : ""}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Create Coleta from WhatsApp Response Dialog */}
			<Dialog open={coletaDialogOpen} onClose={() => setColetaDialogOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<WhatsAppIcon sx={{ color: "#25D366" }} />
						Registrar Coleta via WhatsApp
					</Box>
				</DialogTitle>
				<DialogContent>
					{selectedMessage && (
						<Box sx={{ bgcolor: "#f8f9fa", borderRadius: "8px", p: 2, mb: 2, mt: 1 }}>
							<Typography variant="caption" sx={{ color: "#8e9bae" }}>Resposta de:</Typography>
							<Typography variant="body2" sx={{ fontWeight: 600, color: "#0f4557" }}>{selectedMessage.fornecedor?.nome || "Fornecedor"}</Typography>
							<Typography variant="caption" sx={{ color: "#8e9bae" }}>Recebimento: {formatDateBR(selectedMessage.dataAlvo)}</Typography>
						</Box>
					)}
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						<FormControl fullWidth>
							<InputLabel>Fornecedor</InputLabel>
							<Select value={formFornecedor} onChange={(e) => setFormFornecedor(e.target.value)} label="Fornecedor">
								<MenuItem value="">Sem fornecedor</MenuItem>
								{fornecedores.map((f) => (
									<MenuItem key={f.id} value={f.id}>{f.nome}</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl fullWidth>
							<InputLabel>Produto</InputLabel>
							<Select value={formProduto} onChange={(e) => { setFormProduto(e.target.value); setFormUnidadeMedida(""); }} label="Produto">
								{produtos.map((p) => (
									<MenuItem key={p.id} value={p.id}>{p.cultivo?.cultura || "Produto"}</MenuItem>
								))}
							</Select>
						</FormControl>
						<Box sx={{ display: "flex", gap: 2 }}>
							<TextField label="Quantidade" type="number" value={formQuantidade} onChange={(e) => setFormQuantidade(e.target.value)} sx={{ width: 130, flexShrink: 0 }} />
							<FormControl sx={{ flex: 1, minWidth: 0 }}>
								<InputLabel>Unidade</InputLabel>
								<Select value={formUnidadeMedida} onChange={(e) => setFormUnidadeMedida(e.target.value)} label="Unidade">
									{(() => {
										const produtoSelecionado = produtos.find((p) => p.id === formProduto);
										const embalagens = produtoSelecionado?.embalagens || [];
										const unidadesMap = new Map();
										embalagens.forEach((emb) => {
											if (emb.unidadeMedida && !unidadesMap.has(emb.unidadeMedida.id)) {
												unidadesMap.set(emb.unidadeMedida.id, emb.unidadeMedida);
											}
										});
										const unidadesFiltradas = Array.from(unidadesMap.values());
										if (unidadesFiltradas.length === 0) {
											return unidadesMedida.map((u) => (
												<MenuItem key={u.id} value={u.id}>{u.sigla}</MenuItem>
											));
										}
										return unidadesFiltradas.map((u) => (
											<MenuItem key={u.id} value={u.id}>{u.sigla}</MenuItem>
										));
									})()}
								</Select>
							</FormControl>
						</Box>
						<TextField label="Data Prevista" type="date" value={formDataPrevista} onChange={(e) => setFormDataPrevista(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setColetaDialogOpen(false)}>Cancelar</Button>
					<Button variant="contained" onClick={handleSaveColeta} sx={{ bgcolor: "#1a9e8f" }}>
						Registrar Coleta
					</Button>
				</DialogActions>
			</Dialog>

			{/* Snackbar */}
			<Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
				<Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</AppLayout>
	);
}

export default WhatsApp;
